import {
  customElement,
  html,
  css,
  type TemplateResult,
  DeesElement,
  type CSSResult,
  property,
} from '@design.estate/dees-element';
import * as domtools from '@design.estate/dees-domtools';

import { DeesInputCheckbox } from '../../00group-input/dees-input-checkbox/dees-input-checkbox.js';
import { DeesInputCode } from '../../00group-input/dees-input-code/dees-input-code.js';
import { DeesInputDatepicker } from '../../00group-input/dees-input-datepicker/index.js';
import { DeesInputText } from '../../00group-input/dees-input-text/dees-input-text.js';
import { DeesInputTextarea } from '../../00group-input/dees-input-textarea/dees-input-textarea.js';
import { DeesInputQuantitySelector } from '../../00group-input/dees-input-quantityselector/dees-input-quantityselector.js';
import { DeesInputRadiogroup } from '../../00group-input/dees-input-radiogroup/dees-input-radiogroup.js';
import { DeesInputDropdown } from '../../00group-input/dees-input-dropdown/dees-input-dropdown.js';
import { DeesInputFileupload } from '../../00group-input/dees-input-fileupload/index.js';
import { DeesInputIban } from '../../00group-input/dees-input-iban/dees-input-iban.js';
import { DeesInputMultitoggle } from '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { DeesInputPhone } from '../../00group-input/dees-input-phone/dees-input-phone.js';
import { DeesInputToggle } from '../../00group-input/dees-input-toggle/dees-input-toggle.js';
import { DeesInputTypelist } from '../../00group-input/dees-input-typelist/dees-input-typelist.js';
import { DeesInputTags } from '../../00group-input/dees-input-tags/dees-input-tags.js';
import { DeesInputList } from '../../00group-input/dees-input-list/dees-input-list.js';
import { DeesInputWysiwyg } from '../../00group-input/dees-input-wysiwyg/dees-input-wysiwyg.js';
import { DeesInputRichtext } from '../../00group-input/dees-input-richtext/component.js';
import { DeesFormSubmit } from '../dees-form-submit/dees-form-submit.js';
import { DeesTable } from '../../00group-dataview/dees-table/index.js';
import { demoFunc } from './dees-form.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

// Unified set for form input types
const FORM_INPUT_TYPES = [
  DeesInputCheckbox,
  DeesInputCode,
  DeesInputDatepicker,
  DeesInputDropdown,
  DeesInputFileupload,
  DeesInputIban,
  DeesInputMultitoggle,
  DeesInputPhone,
  DeesInputQuantitySelector,
  DeesInputRadiogroup,
  DeesInputText,
  DeesInputTextarea,
  DeesInputToggle,
  DeesInputTypelist,
  DeesInputTags,
  DeesInputList,
  DeesInputWysiwyg,
  DeesInputRichtext,
  DeesTable,
];

export interface IFormCompatibleElement extends HTMLElement {
  key?: string;
  label?: string;
  value?: unknown;
  required?: boolean;
  disabled?: boolean;
  layoutMode?: 'vertical' | 'horizontal' | 'auto';
  changeSubject?: {
    subscribe: (callback: (...args: unknown[]) => unknown) => domtools.plugins.smartrx.rxjs.Subscription;
  };
  getValue?: () => unknown;
  setValue?: (value: unknown) => void;
  checkValidity?: () => boolean;
  reportValidity?: () => boolean;
  reset?: () => void;
}

export type TFormDataValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | unknown[]
  | File[]
  | { option: string; key: string; payload?: unknown }
  | Record<string, unknown>;

export type TFormData = Record<string, TFormDataValue>;

type TFormElementChangeSubject = {
  subscribe: (callback: () => Promise<void>) => domtools.plugins.smartrx.rxjs.Subscription;
};

export type TFormInputElement =
  | DeesInputCheckbox
  | DeesInputCode
  | DeesInputDatepicker
  | DeesInputDropdown
  | DeesInputFileupload
  | DeesInputIban
  | DeesInputMultitoggle
  | DeesInputPhone
  | DeesInputQuantitySelector
  | DeesInputRadiogroup
  | DeesInputText
  | DeesInputTextarea
  | DeesInputToggle
  | DeesInputTypelist
  | DeesInputTags
  | DeesInputList
  | DeesInputWysiwyg
  | DeesInputRichtext
  | DeesTable<any>
  | IFormCompatibleElement;

function isBuiltInFormInputElement(child: Element): boolean {
  return FORM_INPUT_TYPES.includes(child.constructor as any);
}

function isExternalFormCompatibleElement(child: Element): child is IFormCompatibleElement {
  if (isBuiltInFormInputElement(child)) {
    return false;
  }
  const candidate = child as IFormCompatibleElement;
  return typeof candidate.key === 'string'
    && typeof candidate.changeSubject?.subscribe === 'function'
    && typeof candidate.getValue === 'function'
    && typeof candidate.setValue === 'function';
}

function isFormInputElement(child: Element): child is TFormInputElement {
  return isBuiltInFormInputElement(child) || isExternalFormCompatibleElement(child);
}

function getFormElementValue(child: TFormInputElement): TFormDataValue {
  if (isExternalFormCompatibleElement(child as Element)) {
    return (child as IFormCompatibleElement).getValue!() as TFormDataValue;
  }
  return (child as { value: TFormDataValue }).value;
}

function setFormElementValue(child: TFormInputElement, value: TFormDataValue): void {
  if (isExternalFormCompatibleElement(child as Element)) {
    (child as IFormCompatibleElement).setValue!(value);
    return;
  }
  (child as { value: TFormDataValue }).value = value;
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-form': DeesForm;
  }
}

@customElement('dees-form')
export class DeesForm extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Form'];

  public name: string = 'myform';
  /** emits the collected form data on every child change; the only producer is `collectFormData()` */
  public changeSubject = new domtools.plugins.smartrx.rxjs.Subject<TFormData>();
  public readyDeferred = domtools.plugins.smartpromise.defer();

  /**
   * Controls the layout mode of child input components
   * When true, sets all child inputs to horizontal layout
   */
  @property({ type: Boolean, reflect: true, attribute: 'horizontal-layout' })
  accessor horizontalLayout: boolean = false;

  /**
   * Inset-grouped form: renders inputs as hairline-separated label-left
   * rows inside a single rounded card (settings-list style).
   */
  @property({ type: Boolean, reflect: true })
  accessor grouped: boolean = false;

  public static styles = [
    themeDefaultStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        gap: var(--dees-spacing-lg);
        min-width: 0;
      }

      :host([horizontal-layout]) {
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: var(--dees-spacing-lg);
      }

      /* Inset-grouped form: inputs become hairline-separated rows inside a
         single rounded card; rows are label-left with an aligned label column. */
      :host([grouped]) {
        display: block;
        gap: 0;
        background: var(--dees-color-settings-group);
        border: none;
        border-radius: var(--dees-radius-xl);
        /* Keep field focus rings and anchored suggestions outside the row visible. */
        overflow: visible;
        --dees-input-label-width: 38%;
        --dees-label-gap: 0px;
      }

      :host([grouped]) ::slotted(*) {
        position: relative;
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: 44px;
        box-sizing: border-box;
        margin: 0 !important;
        padding: var(--dees-spacing-sm) var(--dees-spacing-lg);
        --dees-input-control-bg: transparent;
        --dees-input-control-border: transparent;
        --dees-input-control-shadow: none;
        --dees-input-control-align: right;
        --dees-input-control-height: 28px;
        --dees-input-control-max-width: 220px;
      }

      :host([grouped]) ::slotted(*)::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 16px;
        right: 0;
        height: 1px;
        background: var(--dees-color-border-subtle);
        pointer-events: none;
      }
      :host([grouped]) ::slotted(*:last-child)::after { display: none; }
      :host([grouped]) ::slotted([input-layout="compound"]) {
        --dees-input-control-bg: var(--dees-color-control-bg);
        --dees-input-control-border: var(--dees-color-control-border);
        --dees-input-control-align: left;
        --dees-input-control-max-width: none;
        padding-block: 16px;
      }
      @media (max-width: 420px) {
        :host([grouped]) { --dees-input-label-width: 44%; }
        :host([grouped]) ::slotted(*) { padding: 12px; }
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <slot></slot>
    `;
  }

  constructor() {
    super();
    // The child subscriptions and the delegated listeners belong to a
    // connection, not to the first render. `DeesElement` releases
    // `rxSubscriptions` on every disconnect, and `firstUpdated()` never runs a
    // second time, so a form that is moved in the DOM or re-attached (a cached
    // view, a re-opened panel) stopped reporting changes. Establishing them in
    // a startup function re-runs them on every connection, and the connection's
    // abort signal takes both away again, so re-attaching cannot accumulate
    // duplicates.
    this.registerStartupFunction(async (signal) => {
      await this.updateComplete;
      if (signal.aborted || !this.isConnected) return;
      this.applyChildState();

      for (const child of this.getFormElements()) {
        const childSubscription = (child.changeSubject as TFormElementChangeSubject).subscribe(async () => {
          const valueObject = await this.collectFormData();
          this.changeSubject.next(valueObject);
          this.updateRequiredStatus();
        });
        this.rxSubscriptions.push(childSubscription);
      }
      await this.addBehaviours(signal);
      this.readyDeferred.resolve();
    });
  }

  /**
   * The state the form derives from its children. It belongs to the rendered
   * form, so the first render applies it without waiting for the connection's
   * startup work: the submit button reports the required state as soon as the
   * form is on screen. Every call is idempotent.
   */
  public async firstUpdated() {
    this.applyChildState();
  }

  private applyChildState(): void {
    void this.updateRequiredStatus();
    this.updateChildrenLayoutMode();
    this.applyGroupedMode();
  }

  public getFormElements(): Array<TFormInputElement> {
    // Use querySelectorAll('*') to find form inputs nested inside wrapper elements (e.g. <div>)
    return Array.from(this.querySelectorAll('*')).filter((child) =>
      isFormInputElement(child)
    ) as unknown as TFormInputElement[];
  }

  public getSubmitButton(): DeesFormSubmit | undefined {
    return Array.from(this.querySelectorAll('*')).find(
      (child) => child instanceof DeesFormSubmit
    ) as DeesFormSubmit;
  }

  public async updateRequiredStatus() {
    let requiredOK = true;
    for (const childArg of this.getFormElements()) {
      if (childArg.disabled) continue;
      if ((childArg.required && !getFormElementValue(childArg))
        || (childArg as IFormCompatibleElement).checkValidity?.() === false) {
        requiredOK = false;
      }
    }
    const submitButton = this.getSubmitButton();
    if (submitButton) {
      submitButton.disabled = !requiredOK;
    }
  }

  /**
   * collects the form data
   * @returns
   */
  public async collectFormData() {
    const children = this.getFormElements();
    const valueObject: TFormData = {};
    
    for (const child of children) {
      if (!child.key) {
        console.log(`form element with label "${child.label}" has no key. skipping.`);
        continue;
      }
      
      valueObject[child.key] = getFormElementValue(child);
    }
    
    return valueObject;
  }

  public async gatherAndDispatch() {
    for (const child of this.getFormElements()) {
      if (child.disabled) continue;
      const input = child as IFormCompatibleElement;
      if (input.checkValidity?.() === false) {
        input.reportValidity?.();
        child.focus();
        return;
      }
    }
    const valueObject = await this.collectFormData();
    const formDataEvent = new CustomEvent('formData', {
      detail: {
        data: valueObject,
      },
      bubbles: true,
    });
    this.dispatchEvent(formDataEvent);
  }

  public setStatus(
    visualStateArg: 'normal' | 'pending' | 'error' | 'success',
    textStateArg: string
  ) {
    const inputChildren = this.getFormElements();
    const submitButton = this.getSubmitButton();
    if (!submitButton) return;

    switch (visualStateArg) {
      case 'normal':
        submitButton.disabled = false;
        submitButton.status = 'normal';
        for (const inputChild of inputChildren) {
          inputChild.disabled = false;
        }
        break;
      case 'pending':
        submitButton.disabled = true;
        submitButton.status = 'pending';
        for (const inputChild of inputChildren) {
          inputChild.disabled = true;
        }
        break;
      case 'success':
        submitButton.disabled = true;
        submitButton.status = 'success';
        for (const inputChild of inputChildren) {
          inputChild.disabled = true;
        }
        break;
      case 'error':
        submitButton.disabled = true;
        submitButton.status = 'error';
        for (const inputChild of inputChildren) {
          inputChild.disabled = true;
        }
        break;
    }

    submitButton.text = textStateArg;
  }

  /**
   * resets the form
   */
  reset() {
    const inputChildren = this.getFormElements();

    for (const inputChild of inputChildren) {
      const input = inputChild as IFormCompatibleElement;
      if (input.reset) input.reset();
      else setFormElementValue(inputChild, null);
    }
    this.setStatus('normal', 'Submit');
    void this.updateRequiredStatus();
  }

  /**
   * Delegated listeners for the form's children. They belong to one connection:
   * pass the connection's abort signal so a re-attached form installs them once
   * more instead of handling every key twice.
   */
  public async addBehaviours(signal?: AbortSignal) {
    this.addEventListener('validation-change', event => {
      if (this.getFormElements().includes(event.target as TFormInputElement)) void this.updateRequiredStatus();
    }, { signal });
    // Use event delegation
    this.addEventListener('keydown', (event: KeyboardEvent) => {
      const target = event.target as DeesElement;
      if (!isFormInputElement(target)) return;
      if (event.defaultPrevented || event.isComposing || target.disabled) return;
      const origin = event.composedPath()[0];
      if (origin instanceof HTMLButtonElement) return;
      if (origin instanceof HTMLTextAreaElement || (origin instanceof HTMLElement && origin.isContentEditable)) return;

      if (event.key === 'Enter') {
        event.preventDefault();
        const children = this.getFormElements();
        const currentIndex = children.indexOf(target as any);
        const next = children.slice(currentIndex + 1).find(child => !child.disabled);
        if (next) {
          next.focus();
        } else {
          target.blur();
          void this.getSubmitButton()?.submit();
        }
      }
    }, { signal });
  }

  /**
   * Updates the layout mode of child input components based on form's horizontalLayout property
   */
  private updateChildrenLayoutMode() {
    const formChildren = this.getFormElements();
    for (const child of formChildren) {
      if ('layoutMode' in child) {
        // The child's auto mode will detect this form's horizontal-layout attribute
        (child as any).layoutMode = 'auto';
      }
    }
  }

  /**
   * In grouped mode, render each input as a label-left row (settings-list style).
   */
  private applyGroupedMode() {
    if (!this.grouped) return;
    for (const child of this.getFormElements()) {
      if ('labelPosition' in child) {
        (child as any).labelPosition = 'left';
      }
    }
  }

  /**
   * Called when properties change
   */
  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    
    if (changedProperties.has('horizontalLayout')) {
      this.updateChildrenLayoutMode();
    }
  }
}
